import { FC, useRef, useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as echarts from 'echarts';
import { AnalyticsByCustomerResponse } from './types';
import ChartErrorState from './ChartErrorState';
import { seriesPrimary, seriesSecondary, chartChrome } from '../../utils/chartColors';
import './ShipmentsByCustomerChart.scss';

interface ShipmentsByCustomerChartProps {
  data: AnalyticsByCustomerResponse | null;
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

const ShipmentsByCustomerChart: FC<ShipmentsByCustomerChartProps> = ({ data, isLoading, error, onRetry }) => {
  const { t } = useTranslation();
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [chartNode, setChartNode] = useState<HTMLDivElement | null>(null);

  const chartCallbackRef = useCallback((node: HTMLDivElement | null) => {
    setChartNode(node);
  }, []);

  useEffect(() => {
    if (!chartNode || !data || data.data.length === 0) {
      return;
    }

    if (!chartInstance.current || chartInstance.current.getDom() !== chartNode) {
      chartInstance.current?.dispose();
      chartInstance.current = echarts.init(chartNode);
    }

    const customers = data.data.map((d) =>
      d.clientId.length > 12 ? `${d.clientId.substring(0, 12)}...` : d.clientId
    );
    const currentValues = data.data.map((d) => d.currentValue);

    const primary = seriesPrimary();
    const secondary = seriesSecondary();
    const chrome = chartChrome();

    const series: echarts.BarSeriesOption[] = [
      {
        type: 'bar',
        name: String(t('cpi.analytics.currentYear')),
        data: currentValues,
        itemStyle: { color: primary },
        barMaxWidth: 40,
        label: {
          show: true,
          position: 'top',
          fontSize: 10,
          formatter: (params: any) => (params.value > 0 ? String(Math.round(params.value)) : ''),
        },
      },
    ];

    if (data.compareWithPreviousYear) {
      const previousValues = data.data.map((d) => d.previousValue ?? 0);
      series.push({
        type: 'bar',
        name: String(t('cpi.analytics.previousYear')),
        data: previousValues,
        itemStyle: { color: secondary },
        barMaxWidth: 40,
        label: {
          show: true,
          position: 'top',
          fontSize: 10,
          formatter: (params: any) => (params.value > 0 ? String(Math.round(params.value)) : ''),
        },
      });
    }

    const option: echarts.EChartsOption = {
      tooltip: { trigger: 'axis' },
      legend: data.compareWithPreviousYear
        ? { bottom: 0, textStyle: { fontSize: 12 } }
        : undefined,
      grid: {
        left: '3%',
        right: '4%',
        top: '10%',
        bottom: data.compareWithPreviousYear ? '15%' : '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: customers,
        axisLabel: { rotate: 30, fontSize: 11, color: chrome.muted },
      },
      yAxis: {
        type: 'value',
        name: String(t('cpi.axis.shipmentCount')),
        nameTextStyle: { color: chrome.muted, fontSize: 11 },
        axisLabel: { color: chrome.muted },
      },
      series,
    };

    chartInstance.current.setOption(option, true);
  }, [data, chartNode, t]);

  useEffect(() => {
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  if (error && !isLoading) {
    return (
      <div className="shipments-by-customer-chart">
        <h3 className="shipments-by-customer-chart__title">{t('cpi.analytics.shipmentsByCustomer')}</h3>
        <ChartErrorState onRetry={onRetry} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="shipments-by-customer-chart shipments-by-customer-chart--loading">
        <h3 className="shipments-by-customer-chart__title">{t('cpi.analytics.shipmentsByCustomer')}</h3>
        <div className="shipments-by-customer-chart__skeleton" />
      </div>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <div className="shipments-by-customer-chart">
        <h3 className="shipments-by-customer-chart__title">{t('cpi.analytics.shipmentsByCustomer')}</h3>
        <p className="chart-no-data">{t('cpi.noDataAvailableForChart')}</p>
      </div>
    );
  }

  return (
    <div className="shipments-by-customer-chart">
      <h3 className="shipments-by-customer-chart__title">{t('cpi.analytics.shipmentsByCustomer')}</h3>
      <div ref={chartCallbackRef} style={{ height: '350px', width: '100%' }} />
    </div>
  );
};

export default ShipmentsByCustomerChart;
